import { EditDetailsForm } from "@/components/client/forms/edit-details-form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useStore } from "@/store";
import { useEffect, useState } from "react";

import FileUploadAndComments from "@/components/client/forms/file-upload-and-comments";
import Loader from "@/components/utils/loader";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ArrowLeft, AlertCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  downloadLogEventsPdf,
  downloadLogEventsExcel,
} from "@/api/client/communications";
import { FileText, FileSpreadsheet } from "lucide-react";
import {
  getLogEventDisplay,
  getLogEventDisplayMessage,
  getLogEventDisplayResult,
  getLogEventUserDisplayName,
} from "@/utils/log-event-display";

const EditPage = ({ type }) => {
  const { getCommunicationById, currentCommunication, getCompanyData } = useStore((state) => ({
    getCommunicationById: state.getCommunicationById,
    currentCommunication: state.currentCommunication,
    getCompanyData: state.getCompanyData,
  }));
  const { t } = useTranslation();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const tabsList = [
    {
      name: t("edit.editForm.details"),
      id: 0,
    },
    {
      name: t("edit.editForm.publicMessages"),
      id: 1,
    },
    {
      name: t("edit.editForm.privateMessages"),
      id: 2,
    },
    {
      name: t("edit.editForm.log"),
      id: 3,
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        await getCommunicationById(id);
        const companyResult = await Promise.allSettled([getCompanyData()]);
        if (companyResult[0]?.status === "rejected") {
          console.warn("⚠️ EditPage: no se pudieron refrescar los datos de empresa");
        }
      } catch (error) {
        console.error("❌ EditPage: Error con la carga", error);
        setError(t("errors.loadingCommunication"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getCommunicationById, getCompanyData, id, t]);

  const handleTabChange = (value) => {
    setActiveTab(value);
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoBack = () => {
    const redirectPath = currentCommunication?.type === "complaint" 
      ? "/admin/complaints" 
      : "/admin/queries";
    navigate(redirectPath);
  };

  const handleDownloadLogEvents = async (format) => {
    const isPdf = format === "pdf";
    try {
      const blob = isPdf
        ? await downloadLogEventsPdf(id)
        : await downloadLogEventsExcel(id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `communication_${id}_historico.${isPdf ? "pdf" : "xlsx"}`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error al exportar el historial de la comunicación:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center h-screen">
        <Loader style={"loader"} />
      </div>
    );
  }

  if (error || !currentCommunication) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t("errors.errorTitle")}</h2>
        <p className="text-gray-600">{error || t("errors.communicationNotFound")}</p>
        <div className="flex gap-4 mt-4">
          <Button onClick={handleRetry}>
            {t("buttonTexts.retry")}
          </Button>
          <Button variant="outline" onClick={handleGoBack}>
            {t("buttonTexts.goBack")}
          </Button>
        </div>
      </div>
    );
  }

  const communicationType = currentCommunication?.type === "complaint" ? "complaints" : "queries";

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-10">
      <section className="flex flex-col gap-6">
        <h1 className="flex items-center gap-2">
          <Link
            to={`/admin/${communicationType}`}
            className="flex"
          >
            <ArrowLeft className="ml-2 h-6 w-6" />
          </Link>
          {currentCommunication?.type === "complaint"
            ? t("edit.editForm.complaintDetail")
            : t("edit.editForm.querieDetail")}
          : {id}
        </h1>
        <div className="mx-auto grid w-full items-start gap-6 mt-6">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="flex-wrap">
              {tabsList.map((item) => {
                return (
                  <TabsTrigger key={item.id} value={item.id}>
                    <NavLink className="flex items-center gap-2 mx-1">
                      {({ isActive }) => <span>{t(item.name)}</span>}
                    </NavLink>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            <TabsContent key={"tab-0"} value={0} className="mt-5">
              <EditDetailsForm fetchData={() => getCommunicationById(id)} />
            </TabsContent>
            <TabsContent key={"tab-1"} value={1} className="mt-5">
              <FileUploadAndComments viewType="public" isAdmin={true} communication={currentCommunication} />
            </TabsContent>
            <TabsContent key={"tab-2"} value={2} className="mt-5">
              <FileUploadAndComments viewType="private" isAdmin={true} communication={currentCommunication} />
            </TabsContent>
            <TabsContent key={"tab-3"} value={3} className="mt-5">
              <div className="flex items-center justify-end gap-2 mb-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadLogEvents("pdf")}
                        className="flex items-center gap-1"
                        aria-label={t("edit.log.exportPdf")}
                      >
                        <FileText className="h-4 w-4" />
                        <span>{t("edit.log.exportPdf")}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("edit.log.exportPdf")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadLogEvents("excel")}
                        className="flex items-center gap-1"
                        aria-label={t("edit.log.exportExcel")}
                      >
                        <FileSpreadsheet className="h-4 w-4" />
                        <span>{t("edit.log.exportExcel")}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("edit.log.exportExcel")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="border border-dashed rounded-lg my-6 p-6 border-primary">
                {currentCommunication?.logEvents?.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="text-left">{t("edit.log.date")}</th>
                        <th className="text-left">{t("edit.log.userName")}</th>
                        <th className="text-left">
                          {t("edit.log.message")}
                        </th>
                        <th className="text-left">{t("edit.log.eventName")}</th>
                        <th className="text-left">{t("edit.log.result")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentCommunication?.logEvents
                        ?.sort(
                          (a, b) =>
                            new Date(b?.eventDateTime || 0) -
                            new Date(a?.eventDateTime || 0),
                        )
                        .map((log, index) => (
                          <tr key={log?.id || index}>
                            <td className="border-b border-gray-200">
                              {log?.eventDateTime &&
                                format(
                                  new Date(log?.eventDateTime || ""),
                                  "dd '-' MM '-' yyyy HH:mm:ss",
                                  {
                                    locale: es,
                                  },
                                )}
                            </td>
                            <td className="border-b border-gray-200">
                              {getLogEventUserDisplayName(log, t)}
                            </td>
                            <td className="border-b border-gray-200">
                              {log?.eventName === 'email.sent' ? (
                                <Dialog>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <DialogTrigger asChild>
                                        <TooltipTrigger asChild>
                                          <span className="cursor-pointer underline decoration-dotted underline-offset-4">
                                            {getLogEventDisplayMessage(t, log)}
                                          </span>
                                        </TooltipTrigger>
                                      </DialogTrigger>
                                      <TooltipContent className="max-w-md break-words">
                                        <p className="text-sm font-semibold mb-1 border-b pb-1">{log?.emailSubject || ''}</p>
                                        <div className="text-sm" dangerouslySetInnerHTML={{ __html: log?.emailContent || '' }} />
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                    <DialogHeader>
                                      <DialogTitle>{log?.emailSubject || ''}</DialogTitle>
                                    </DialogHeader>
                                    <div className="break-words text-sm" dangerouslySetInnerHTML={{ __html: log?.emailContent || '' }} />
                                  </DialogContent>
                                </Dialog>
                              ) : (
                                getLogEventDisplayMessage(t, log)
                              )}
                            </td>
                            <td className="border-b border-gray-200">
                              {getLogEventDisplay(t, log)}
                            </td>
                            <td className="border-b border-gray-200">
                              {getLogEventDisplayResult(t, log)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                ) : (
                  <p>{t("edit.log.noEvents")}</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default EditPage;
